/* eslint-disable no-useless-escape */
import dayjs from "dayjs";
import axios from "../config/httpConfig";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(quarterOfYear);
export const DateFormatter = (date) => {
  return new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/** Date only (no time), long month — e.g. "August 20, 2026" */
export const formatDateLong = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/** Date only (no time), short month — e.g. "Aug 20, 2026" (matches Deal Booked style) */
export const formatDateShort = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB").format(date);
};

export const formatTime = (timeInSeconds) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0",
  )}`;
};

export const formatTimeAgo = (timestamp) => {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return "just now";
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60);
    return `${mins} min${mins > 1 ? "s" : ""} ago`;
  }
  if (diffSec < 86400) {
    const hrs = Math.floor(diffSec / 3600);
    return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  }
  const days = Math.floor(diffSec / 86400);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

export const dealsClosesIn = (date) => {
  const now = dayjs();
  const expiryDate = dayjs(date).endOf("day");

  const diffInDays = expiryDate.diff(now, "day");

  if (diffInDays > 0) {
    return `${diffInDays} days`;
  }

  const diffInHours = expiryDate.diff(now, "hour");
  if (diffInHours > 0) {
    return `${diffInHours} hrs`;
  }

  const diffInMinutes = expiryDate.diff(now, "minute");
  return diffInMinutes > 0 ? `${diffInMinutes} mins` : "Expired";
};

export const formatCurrency = (amount) => {
  let num = Number(amount);

  // If negative small amount (like -0.26, -0.9) → treat as 0
  if (num > -1 && num < 1) {
    num = 0;
  }

  // If exactly -0 → convert to 0
  if (Object.is(num, -0)) {
    num = 0;
  }

  const rounded = Math.round(num);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rounded);
};

export const formatCurrencyTwoDec = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatToLacCr = (amount) => {
  if (
    amount === undefined ||
    amount === null ||
    amount === "" ||
    Number.isNaN(Number(amount))
  ) {
    return "";
  }
  const num = Number(amount);
  if (num >= 10000000) {
    return (num / 10000000).toFixed(2) + " Cr"; // For Crores
  } else if (num >= 100000) {
    return (num / 100000).toFixed(2) + " Lac"; // For Lakhs
  }
  return new Intl.NumberFormat("en-IN").format(num); // For smaller amounts
};

export const formatToLCr = (amount) => {
  if (
    amount === undefined ||
    amount === null ||
    amount === "" ||
    Number.isNaN(Number(amount))
  ) {
    return "";
  }
  const num = Number(amount);
  if (num >= 10000000) {
    return (num / 10000000).toFixed(0) + " Cr"; // For Crores
  } else if (num >= 100000) {
    return (num / 100000).toFixed(0) + " L"; // For Lakhs
  }
  return new Intl.NumberFormat("en-IN").format(num); // For smaller amounts
};

export const truncateDescription = (description, limit) => {
  if (description?.length <= limit) {
    return description;
  }
  return `${description?.slice(0, limit)}...`;
};

export const calculateSoldOutPercentage = (fundedAmt, approvedFundAmt) => {
  const funded = Number(fundedAmt) || 0;
  const approved = Number(approvedFundAmt);
  if (!approved || !Number.isFinite(approved)) return 0;
  return Math.min((funded / approved) * 100, 100).toFixed(0);
};

export const calculateReturns = (annualDiscountRate) => {
  return (Number(annualDiscountRate) * 100).toFixed(2);
};

export const calculateDiscountAmount = (
  dealAmount,
  annualDiscountRate,
  tenure,
) => {
  return (
    (Number(dealAmount) * Number(annualDiscountRate) * Number(tenure)) /
    365
  ).toFixed(2);
};

export const calculateAvailableAmountToInvest = (
  approvedFundAmt,
  fundedAmt,
) => {
  return (Number(approvedFundAmt) - Number(fundedAmt)).toFixed(2);
};
export const calculateAmountInvestPercentage = (annualDiscountRate, tenure) => {
  return 1 - (Number(annualDiscountRate) * Number(tenure)) / 365;
};
export const calculateYourDealAmount = (
  partAmountToInvest,
  amountInvestPercentage,
) => {
  return (Number(partAmountToInvest) / Number(amountInvestPercentage)).toFixed(
    2,
  );
};

export const calculateAmountToInvestNow = (dealAmount, discountAmount) => {
  return (Number(dealAmount) - Number(discountAmount)).toFixed(2);
};
// export const calculateYourDealAmount = (amountToInvestNow, discountAmount) => {
//   return (Number(amountToInvestNow) + Number(discountAmount)).toFixed(2);
// };
export const calculateNetReturns = (
  discountAmount,
  platformFee,
  penalCharge,
) => {
  return (
    Number(discountAmount) -
    (Number(platformFee) + Number(penalCharge))
  ).toFixed(2);
};

export const calculatePlatformFee = (dealAmount, platformFee, tenure) => {
  return (
    ((Number(dealAmount) * Number(platformFee)) / 365) *
    Number(tenure)
  ).toFixed(2);
};

export const calculateExpectedNetProfit = (discountAmount, platformFee) => {
  return (Number(discountAmount) - Number(platformFee)).toFixed(2);
};

export const calculateExpectedNetRepayment = (
  amountToInvestNow,
  discountAmount,
  platformFee,
) => {
  return (
    Number(amountToInvestNow) +
    (Number(discountAmount) - Number(platformFee))
  ).toFixed(2);
};

export const getComparisonFilterId = (filterId) => {
  const comparisonMap = {
    ORV0004: "ORV0002", // This Month vs last Month -> last Month
    ORV0001: "ORV0006", // Today -> Yesterday
    ORV0006: "ORV0007", // Yesterday -> Day before (handled specially)
    ORV0007: "ORV0003", // Last 7 Days -> previous 7 Days
    ORV0003: "ORV0006", // Last 30 Days -> last Month
    ORV0005: "ORV0002", // This Month -> last Month
    ORV0008: "ORV0008", // This Quarter vs Previous Quarter -> use same
    ORV0009: "ORV0009", // This Year vs Last Year -> use same
    ORV0002: null, // Till now -> no comparison
  };
  return comparisonMap[filterId] || null;
};

export const calculateDateRangeForComparison = (
  filterId,
  currentStart,
  currentEnd,
) => {
  const now = dayjs();

  switch (filterId) {
    case "ORV0001": // Today comparison -> Yesterday
      return {
        start: now.subtract(1, "day").startOf("day").format("YYYY-MM-DD"),
        end: now.subtract(1, "day").endOf("day").format("YYYY-MM-DD"),
      };
    case "ORV0006": // Yesterday comparison -> Day before yesterday
      return {
        start: now.subtract(2, "day").startOf("day").format("YYYY-MM-DD"),
        end: now.subtract(2, "day").endOf("day").format("YYYY-MM-DD"),
      };
    case "ORV0007": // Last 7 Days comparison -> previous 7 Days
      return {
        start: now.subtract(13, "day").startOf("day").format("YYYY-MM-DD"),
        end: now.subtract(7, "day").endOf("day").format("YYYY-MM-DD"),
      };
    case "ORV0003": // Last 30 Days comparison -> previous 30 Days
      return {
        start: now.subtract(59, "day").startOf("day").format("YYYY-MM-DD"),
        end: now.subtract(30, "day").endOf("day").format("YYYY-MM-DD"),
      };
    case "ORV0004": // This Month -> last Month
      return {
        start: now.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        end: now.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      };
    case "ORV0005": // This Month -> last Month
      return {
        start: now.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        end: now.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      };
    case "ORV0008": // This Quarter -> Previous Quarter
      return {
        start: now
          .subtract(1, "quarter")
          .startOf("quarter")
          .format("YYYY-MM-DD"),
        end: now.subtract(1, "quarter").endOf("quarter").format("YYYY-MM-DD"),
      };
    case "ORV0009": // This Year -> Last Year
      return {
        start: now.subtract(1, "year").startOf("year").format("YYYY-MM-DD"),
        end: now.subtract(1, "year").endOf("year").format("YYYY-MM-DD"),
      };
    default:
      return null;
  }
};
export const calculateDateRange = (filterId) => {
  const now = dayjs();
  let start, end;

  switch (filterId) {
    case "ORV0001": // Today
      start = now.startOf("day").format("YYYY-MM-DD");
      end = now.endOf("day").format("YYYY-MM-DD");
      break;

    case "ORV0002": // Till now
      start = "";
      end = "";
      break;

    case "ORV0003": // Last 30 Days
      start = now.subtract(29, "day").startOf("day").format("YYYY-MM-DD");
      end = now.endOf("day").format("YYYY-MM-DD");
      break;

    case "ORV0004": // This Month vs Last Month
    case "ORV0005": // This Month
      start = now.startOf("month").format("YYYY-MM-DD");
      end = now.endOf("month").format("YYYY-MM-DD");
      break;

    case "ORV0006": // Yesterday
      start = now.subtract(1, "day").startOf("day").format("YYYY-MM-DD");
      end = now.subtract(1, "day").endOf("day").format("YYYY-MM-DD");
      break;

    case "ORV0007": // Last 7 Days
      start = now.subtract(6, "day").startOf("day").format("YYYY-MM-DD");
      end = now.endOf("day").format("YYYY-MM-DD");
      break;

    case "ORV0008": // This Quarter vs Previous Quarter
      start = now.startOf("quarter").format("YYYY-MM-DD");
      end = now.endOf("quarter").format("YYYY-MM-DD");
      break;

    case "ORV0009": // This Year vs Last Year
      start = now.startOf("year").format("YYYY-MM-DD");
      end = now.endOf("year").format("YYYY-MM-DD");
      break;

    default:
      start = "";
      end = "";
      break;
  }

  return { start, end };
};

export const formatCategoryName = (category) => {
  if (category.toLowerCase() === "roi") {
    return category.toUpperCase();
  }

  if (category === "industryTypes") {
    return "Industries";
  }

  return category
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatOptionLabel = (category, label) => {
  // Append "%" for ROI and "days" for Maturity
  if (category === "roi") {
    return `${label}%`;
  }
  if (category === "maturity") {
    return `${label} days`;
  }
  return label;
};

export const getUserLocation = async () => {
  if ("geolocation" in navigator) {
    const getCoords = () =>
      new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve(position.coords),
          (error) => {
            if (error.code === error.TIMEOUT) {
              navigator.geolocation.getCurrentPosition(
                (position) => resolve(position.coords),
                reject,
                {
                  enableHighAccuracy: false,
                  timeout: 10000,
                  maximumAge: 300000,
                },
              );
            } else {
              reject(error);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 30000,
          },
        );
      });

    try {
      const { latitude, longitude } = await getCoords();

      // Reverse geocode using Nominatim (OpenStreetMap)
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=en`;

      const response = await fetch(url, {
        headers: {
          "User-Agent": "MyReactApp/1.0 (your-email@example.com)", // required per Nominatim policy
        },
      });

      if (!response.ok) throw new Error("Reverse geocoding failed");

      const data = await response.json();
      const address = data.address || {};
      return {
        city: address.city || address.town || address.village || "",
        region: address.state || "",
        country: address.country || "",
        latitude,
        longitude,
        source: "geolocation+nominatim",
      };
    } catch (err) {
      console.error("Geolocation+Nominatim failed:", err);
    }
  }
};

export const maskEmail = (email) => {
  if (!email) return "";
  const [localPart, domain] = email.split("@");
  return localPart[0] + "****" + localPart.slice(-1) + "@" + domain;
};

export const maskPhone = (phone) => {
  if (!phone) return "";
  return phone.slice(0, 4) + "****" + phone.slice(-2);
};

export const capitalizeWords = (str) => {
  return str
    ?.replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char?.toUpperCase());
};

export const capitalizeEntireWord = (str) => {
  return str
    ?.replace(/_/g, " ")
    .replace(/\b\w+\b/g, (word) => word.toUpperCase());
};

export const formatParticulars = (str) => {
  if (!str) return "";
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/** Collect period/year keys from pivoted financial metric rows (excludes particulars). */
export const getFinancialMetricPeriodKeys = (rows = []) => {
  const periodKeys = [];
  const seen = new Set();

  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== "object") continue;
    for (const key of Object.keys(row)) {
      if (key === "particulars") continue;
      if (!seen.has(key)) {
        seen.add(key);
        periodKeys.push(key);
      }
    }
  }

  return periodKeys.sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
};

/** Build Ant Design table columns from uploaded financial metric rows. */
export const buildFinancialMetricColumns = (rows = [], particularsRender) => {
  const periodKeys = getFinancialMetricPeriodKeys(rows);

  return [
    {
      title: "Particulars",
      dataIndex: "particulars",
      key: "particulars",
      width: 200,
      fixed: "left",
      ...(particularsRender ? { render: particularsRender } : {}),
    },
    ...periodKeys.map((period) => ({
      title: String(period),
      dataIndex: period,
      key: String(period),
      width: 150,
      align: "center",
    })),
  ];
};

export const toTitleCase = (sentence) => {
  if (!sentence || typeof sentence !== "string") return "";

  return sentence
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const generateRandomHash = () => {
  return Math.floor(Math.random() * 9000) + 1000; // Ensures a 4-digit random number
};

export const confirmPasswordRules = [
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value)
        return Promise.reject(new Error("Please confirm your Password!"));
      if (getFieldValue("enterPassword") !== value)
        return Promise.reject(new Error("Passwords do not match!"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  }),
];

export const passwordRules = [
  {
    required: true,
    message: "Password cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 8)
        return Promise.reject(
          new Error(
            "Password Must be at least Min 8 chars, 1 uppercase, 1 number, 1 special character",
          ),
        );
      if (!/[A-Z]/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one uppercase letter!"),
        );
      if (!/[a-z]/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one lowercase letter!"),
        );
      if (!/\d/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one number!"),
        );
      if (!/[@$!%*?&]/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one special character!"),
        );
      if (value.length > 256)
        return Promise.reject(
          new Error("Password cannot exceed 256 characters!"),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const phoneNumberRules = (type) => [
  {
    required: true,
    message: `${type} Phone Number cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^[6-9]\d{9}$/.test(value))
        return Promise.reject(
          new Error(`Please enter a valid ${type} Phone Number`),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

const getTrimmedValue = (value) =>
  typeof value === "string" ? value.trim() : value;

const rejectIfBlank = (value, message) => {
  const trimmed = getTrimmedValue(value);
  if (!trimmed) {
    return value
      ? Promise.reject(new Error(message))
      : Promise.resolve();
  }
  return null;
};

const rejectIfOnlySpecialChars = (value, fieldLabel) => {
  if (/^[^a-zA-Z0-9]+$/.test(value)) {
    return Promise.reject(
      new Error(`${fieldLabel} cannot contain only special characters!`),
    );
  }
  return null;
};

const rejectIfOnlyNumbers = (value, fieldLabel) => {
  if (/^[0-9\s]+$/.test(value)) {
    return Promise.reject(
      new Error(`${fieldLabel} cannot contain only numbers!`),
    );
  }
  return null;
};

export const ifscRules = [
  {
    required: true,
    whitespace: true,
    message: "IFSC code cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "IFSC code cannot be empty!");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (!/^[A-Z]{4}[0][A-Z0-9]{6}$/.test(trimmed))
        return Promise.reject(new Error("Please enter a valid IFSC code"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const accNumberRules = [
  {
    required: true,
    whitespace: true,
    message: "Account number cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "Account number cannot be empty!");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (!/^\d+$/.test(trimmed))
        return Promise.reject(
          new Error("Account number should contain only numbers."),
        );
      if (trimmed.length < 8 || trimmed.length > 16)
        return Promise.reject(new Error("Please enter a valid account number"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const nameRules = (type) => [
  {
    required: true,
    whitespace: true,
    message: `${type} Name cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, `${type} Name cannot be empty!`);
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (trimmed.length < 3)
        return Promise.reject(
          new Error(`${type} Name must be at least 3 characters!`),
        );
      if (trimmed.length > 256)
        return Promise.reject(
          new Error(`${type} Name cannot exceed 256 characters!`),
        );
      if (!/^[a-zA-Z\s]+$/.test(trimmed))
        return Promise.reject(
          new Error(`${type} Name can only contain alphabets and spaces.`),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const emailRules = (type) => [
  {
    required: true,
    message: `${type} Email cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length > 256)
        return Promise.reject(
          new Error(`${type} Email cannot exceed 256 characters!`),
        );
      if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(value))
        return Promise.reject(new Error(`Please enter a valid ${type} Email.`));
      const localPart = value.split("@")[0];
      if (!/[A-Za-z]/.test(localPart)) {
        const emailLabel = type ? `${type} Email` : "Email";
        return Promise.reject(
          new Error(`${emailLabel} cannot contain only numbers`),
        );
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const companyNameRules = (type) => [
  {
    required: true,
    message: `${type} name cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 3)
        return Promise.reject(
          new Error(`${type} name must be at least 3 characters!`),
        );
      if (value.length > 256)
        return Promise.reject(
          new Error(`${type} name cannot exceed 256 characters!`),
        );
      if (/^\d+$/.test(value))
        return Promise.reject(
          new Error(`${type} name cannot contain only numbers!`),
        );
      if (/^[^a-zA-Z0-9]+$/.test(value))
        return Promise.reject(
          new Error(`${type} name cannot contain only special characters!`),
        );
      if (!/[a-zA-Z]/.test(value))
        return Promise.reject(
          new Error(`${type} name must include at least one letter!`),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const designationRules = [
  {
    required: true,
    message: "Designation cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 2)
        return Promise.reject(
          new Error("Designation must be at least 2 characters!"),
        );
      if (value.length > 256)
        return Promise.reject(
          new Error("Designation cannot exceed 256 characters!"),
        );
      if (!/^[a-zA-Z\s]+$/.test(value))
        return Promise.reject(
          new Error("Designation can only contain alphabets and spaces."),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const address1Rules = [
  {
    required: true,
    whitespace: true,
    message: "Address Line 1 cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "Address Line 1 cannot be empty!");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (trimmed.length < 3)
        return Promise.reject(
          new Error("Address Line 1 must be at least 3 characters!"),
        );
      if (trimmed.length > 256)
        return Promise.reject(
          new Error("Address Line 1 cannot exceed 256 characters!"),
        );
      const specialOnly = rejectIfOnlySpecialChars(trimmed, "Address Line 1");
      if (specialOnly) return specialOnly;
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const address2LengthRules = [
  {
    validator(_, value) {
      const trimmed = getTrimmedValue(value);
      if (!trimmed) return Promise.resolve();
      if (trimmed.length < 3)
        return Promise.reject(
          new Error("Address Line 2 must be at least 3 characters!"),
        );
      if (trimmed.length > 256)
        return Promise.reject(
          new Error("Address Line 2 cannot exceed 256 characters!"),
        );
      const specialOnly = rejectIfOnlySpecialChars(trimmed, "Address Line 2");
      if (specialOnly) return specialOnly;
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const address2Rules = [
  {
    required: true,
    whitespace: true,
    message: "Address Line 2 cannot be empty!",
    validateTrigger: "onBlur",
  },
  ...address2LengthRules,
];

export const cityRules = [
  {
    required: true,
    whitespace: true,
    message: "City cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "City cannot be empty!");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (!/^[A-Za-z\s]+$/.test(trimmed))
        return Promise.reject(new Error("City should contain only Alphabets."));
      if (trimmed.length < 3)
        return Promise.reject(new Error("City must be at least 3 characters!"));
      if (trimmed.length > 256)
        return Promise.reject(new Error("City cannot exceed 256 characters!"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const pincodeRules = [
  {
    required: true,
    whitespace: true,
    message: `Pincode cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "Pincode cannot be empty!");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (!/^\d+$/.test(trimmed))
        return Promise.reject(
          new Error("Pincode should contain only numbers."),
        );
      if (trimmed.length !== 6)
        return Promise.reject(new Error("Pincode should be exactly 6 digits."));
      if (/^0+$/.test(trimmed))
        return Promise.reject(new Error("Pincode cannot contain only zeros."));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const panRules = [
  {
    required: true,
    whitespace: true,
    message: "PAN is required.",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "PAN is required.");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(String(trimmed).toUpperCase()))
        return Promise.reject(new Error("Please enter a valid PAN number."));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const hufPanRules = [
  {
    required: true,
    whitespace: true,
    message: "HUF PAN is required.",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "HUF PAN is required.");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (!/^[A-Z]{3}H[A-Z][0-9]{4}[A-Z]$/.test(String(trimmed).toUpperCase()))
        return Promise.reject(
          new Error("Please enter a valid HUF PAN (4th character must be H)."),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const aadhaarNumberRules = [
  {
    required: true,
    whitespace: true,
    message: "Aadhaar number is required.",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "Aadhaar number is required.");
      if (blank) return blank;
      if (!/^[0-9]{12}$/.test(String(value).trim())) {
        return Promise.reject(
          new Error("Please enter a valid 12-digit Aadhaar number."),
        );
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const idNumberRules = (_idType) => aadhaarNumberRules;

export const bankNameRules = [
  {
    required: true,
    whitespace: true,
    message: "Bank name is required.",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "Bank name is required.");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (trimmed.length < 2) {
        return Promise.reject(new Error("Please enter a valid bank name."));
      }
      if (trimmed.length > 256) {
        return Promise.reject(
          new Error("Bank name cannot exceed 256 characters!"),
        );
      }
      const numbersOnly = rejectIfOnlyNumbers(trimmed, "Bank name");
      if (numbersOnly) return numbersOnly;
      const specialOnly = rejectIfOnlySpecialChars(trimmed, "Bank name");
      if (specialOnly) return specialOnly;
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const branchNameRules = [
  {
    required: true,
    whitespace: true,
    message: "Branch name is required.",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "Branch name is required.");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (trimmed.length < 2) {
        return Promise.reject(new Error("Please enter a valid branch name."));
      }
      if (trimmed.length > 256) {
        return Promise.reject(
          new Error("Branch name cannot exceed 256 characters!"),
        );
      }
      const numbersOnly = rejectIfOnlyNumbers(trimmed, "Branch name");
      if (numbersOnly) return numbersOnly;
      const specialOnly = rejectIfOnlySpecialChars(trimmed, "Branch name");
      if (specialOnly) return specialOnly;
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const MANUAL_KYC_ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/pdf",
];

export const MANUAL_KYC_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export const validateManualKycUploadFile = (file) => {
  if (!file) return "Please upload a file.";
  const type = file.type || "";
  const size = file.size || 0;
  if (!MANUAL_KYC_ALLOWED_MIME_TYPES.includes(type)) {
    return "Only JPEG, PNG, WEBP, or PDF files are allowed.";
  }
  if (size > MANUAL_KYC_MAX_FILE_SIZE_BYTES) {
    return "File size must be smaller than 5 MB.";
  }
  return null;
};

export const gstinRules = [
  {
    required: true,
    whitespace: true,
    message: "GSTIN cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const blank = rejectIfBlank(value, "GSTIN cannot be empty!");
      if (blank) return blank;
      const trimmed = getTrimmedValue(value);
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(trimmed))
        return Promise.reject(new Error("Please enter a valid GSTIN"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export async function handleUploadFile(file, path, onProgress) {
  let fileName;
  const res = await axios.post("/api/fileUpload", {
    fileName: file.name,
    fileType: file.type,
    folderPath: path,
  });
  const { uploadUrl, fileUrl } = res;

  fileName = file.name || `merged_${Date.now()}.pdf`;

  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": file.type,
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress(percentCompleted);
      }
    },
  });

  return { fileUrl, fileName };
}

export const getDownloadSuccessMessage = (fileName) =>
  `${fileName} downloaded successfully`;

export const downloadBlob = (data, fileName) => {
  const blob = data instanceof Blob ? data : new Blob([data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const getDownloadErrorMessage = async (error, fallbackMessage) => {
  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (error?.error) {
    return error.error;
  }

  if (error?.message && typeof error.message === "string") {
    return error.message;
  }

  if (error instanceof Blob) {
    try {
      const text = await error.text();
      const parsed = JSON.parse(text);
      return parsed.error || parsed.message || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  }

  return fallbackMessage;
};

export const validateDownloadBlob = async (blob) => {
  if (!blob) {
    throw new Error("No file received from server.");
  }

  if (blob instanceof Blob && blob.type?.includes("application/json")) {
    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      throw new Error(parsed.error || parsed.message || "Download failed.");
    } catch (parseError) {
      if (
        parseError instanceof Error &&
        parseError.message !== "Download failed."
      ) {
        throw parseError;
      }
      throw new Error("Download failed.");
    }
  }
};

export const feedbackRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Feedback cannot be empty!");
      }

      if (value.length > 500) {
        return Promise.reject("Feedback cannot exceed 500 characters.");
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];
